import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from 'primereact/button';
import { Checkbox } from 'primereact/checkbox';
import { InputTextarea } from 'primereact/inputtextarea';
import { Tag } from 'primereact/tag';
import { Toast } from 'primereact/toast';
import { Card } from 'primereact/card';
import api from '../../services/api';
import './CommunityModerationDetail.css';

function formatWait(minutes) {
  if (minutes == null) return null;
  if (minutes < 60) return `${minutes} min`;
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return m ? `${h} h ${m} min` : `${h} h`;
}

function statusSeverity(status) {
  if (status === 'approved') return 'success';
  if (status === 'partially_approved') return 'info';
  if (status === 'rejected') return 'danger';
  return 'warning';
}

function buildFacebookCaption(report, sections) {
  const lines = [];
  const crossing = report.crossingName || report.port || 'Garita';
  const lane = [report.transportMode, report.laneType].filter(Boolean).join(' · ');
  lines.push(`📍 ${crossing}${lane ? ` · ${lane}` : ''}`);

  if (sections.waitBucketApproved && report.waitBucketMinutes != null) {
    lines.push(`⏱ Esperado para cruzar: ${formatWait(report.waitBucketMinutes)}`);
  }
  if (sections.waitingFromPositionApproved && report.waitingFromPositionMinutes != null) {
    lines.push(`🧍 Desde mi posición: ${formatWait(report.waitingFromPositionMinutes)}`);
  }
  if (sections.textApproved && report.text?.trim()) {
    lines.push('');
    lines.push(report.text.trim());
  }
  lines.push('');
  lines.push('#BorderGarita #Garitas #TiemposDeEspera');
  return lines.join('\n');
}

async function blobToPngBlob(blob) {
  if (blob.type === 'image/png') return blob;

  const bitmap = await createImageBitmap(blob);
  const canvas = document.createElement('canvas');
  canvas.width = bitmap.width;
  canvas.height = bitmap.height;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Canvas not available');
  ctx.drawImage(bitmap, 0, 0);
  bitmap.close?.();

  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (png) => (png ? resolve(png) : reject(new Error('PNG encode failed'))),
      'image/png'
    );
  });
}

async function fetchReportImageBlob(photoUrl) {
  const { data } = await api.get('/community_reports/proxy-image', {
    params: { url: photoUrl },
    responseType: 'blob',
  });
  return data;
}

async function copyImageToClipboard(photoUrl) {
  if (
    typeof ClipboardItem === 'undefined' ||
    !navigator.clipboard ||
    typeof navigator.clipboard.write !== 'function'
  ) {
    throw new Error('Clipboard images not supported in this browser');
  }

  const rawBlob = await fetchReportImageBlob(photoUrl);
  const pngBlob = await blobToPngBlob(rawBlob);
  await navigator.clipboard.write([
    new ClipboardItem({ 'image/png': pngBlob }),
  ]);
}

async function copyTextToClipboard(text) {
  await navigator.clipboard.writeText(text);
}

export default function CommunityModerationDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const toast = useRef(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [copyingImage, setCopyingImage] = useState(false);
  const [copyingCaption, setCopyingCaption] = useState(false);
  const [report, setReport] = useState(null);
  const [note, setNote] = useState('');
  const [sections, setSections] = useState({
    textApproved: true,
    photoApproved: true,
    lineStartApproved: true,
    waitBucketApproved: true,
    waitingFromPositionApproved: true,
    locationApproved: true,
  });

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/community_reports/${id}`);
        setReport(data);
        setNote(data.moderation?.note || '');
        setSections({
          textApproved: data.moderation?.textApproved !== false,
          photoApproved: data.moderation?.photoApproved !== false,
          lineStartApproved: data.moderation?.lineStartApproved !== false,
          waitBucketApproved: data.moderation?.waitBucketApproved !== false,
          waitingFromPositionApproved:
            data.moderation?.waitingFromPositionApproved !== false,
          locationApproved: data.moderation?.locationApproved !== false,
        });
      } catch (err) {
        toast.current?.show({ severity: 'error', summary: 'Error', detail: 'Failed to load' });
      } finally {
        setLoading(false);
      }
    })();
  }, [id]);

  const moderate = async (action) => {
    try {
      setSaving(true);
      const { data } = await api.put(`/community_reports/${id}/moderate`, {
        action,
        note,
        ...sections,
      });
      setReport(data);
      toast.current?.show({
        severity: 'success',
        summary: 'Done',
        detail: action === 'reject' ? 'Rejected' : `Status: ${data.status}`,
      });
    } catch (err) {
      const detail =
        err?.response?.data?.error ||
        err?.response?.data?.message ||
        err?.message ||
        'Failed to moderate';
      toast.current?.show({ severity: 'error', summary: 'Error', detail });
    } finally {
      setSaving(false);
    }
  };

  const handleCopyImage = async () => {
    if (!report?.photoUrl) {
      toast.current?.show({
        severity: 'warn',
        summary: 'Sin foto',
        detail: 'Este reporte no tiene imagen para copiar',
      });
      return;
    }
    try {
      setCopyingImage(true);
      await copyImageToClipboard(report.photoUrl);
      toast.current?.show({
        severity: 'success',
        summary: 'Imagen copiada',
        detail: 'Lista para pegar en Facebook',
      });
    } catch (err) {
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: err?.message || 'No se pudo copiar la imagen',
      });
    } finally {
      setCopyingImage(false);
    }
  };

  const handleCopyCaption = async () => {
    if (!report) return;
    try {
      setCopyingCaption(true);
      const caption = buildFacebookCaption(report, sections);
      await copyTextToClipboard(caption);
      toast.current?.show({
        severity: 'success',
        summary: 'Caption copiado',
        detail: 'Texto listo para pegar en Facebook',
      });
    } catch (err) {
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: 'No se pudo copiar el texto',
      });
    } finally {
      setCopyingCaption(false);
    }
  };

  if (loading) {
    return (
      <Card>
        <div className="card p-4">
          <div
            className="flex flex-column align-items-center justify-content-center"
            style={{ minHeight: '300px' }}
          >
            <i className="pi pi-spin pi-spinner" style={{ fontSize: '2rem', color: '#2020A0' }} />
            <div style={{ marginTop: '1rem', fontSize: '1.2rem', color: '#555' }}>
              Loading report...
            </div>
          </div>
        </div>
      </Card>
    );
  }

  if (!report) {
    return (
      <Card>
        <div className="card p-4">
          <Button
            label="Back"
            icon="pi pi-arrow-left"
            className="p-button-text p-button-sm px-0"
            onClick={() => navigate('/community_moderation')}
          />
          <p>Report not found.</p>
        </div>
      </Card>
    );
  }

  const sectionRows = [
    {
      key: 'textApproved',
      label: 'Text',
      present: !!report.text?.trim(),
      preview: report.text,
    },
    {
      key: 'photoApproved',
      label: 'Photo',
      present: !!report.photoUrl,
      preview: report.photoUrl,
    },
    {
      key: 'lineStartApproved',
      label: 'Line start',
      present: !!report.lineStartLocation,
      preview: report.lineStartLocation
        ? `${Number(report.lineStartLocation.coordinates?.[1]).toFixed(5)}, ${Number(report.lineStartLocation.coordinates?.[0]).toFixed(5)}`
        : null,
    },
    {
      key: 'waitBucketApproved',
      label: 'Wait to cross',
      present: report.waitBucketMinutes != null,
      preview:
        report.waitBucketMinutes != null ? formatWait(report.waitBucketMinutes) : null,
    },
    {
      key: 'waitingFromPositionApproved',
      label: 'From position',
      present: report.waitingFromPositionMinutes != null,
      preview:
        report.waitingFromPositionMinutes != null
          ? formatWait(report.waitingFromPositionMinutes)
          : null,
    },
    {
      key: 'locationApproved',
      label: 'Location',
      present: !!report.location,
      preview: report.location
        ? `${Number(report.location.coordinates?.[1]).toFixed(5)}, ${Number(report.location.coordinates?.[0]).toFixed(5)}`
        : null,
    },
  ];

  const userName = report.userId?.displayName || report.userName || 'User';
  const crossing = report.crossingName || report.port || '—';

  return (
    <Card>
      <div className="card p-4 review-page">
      <Toast ref={toast} position="top-center" />

      <div className="grid mb-4">
        <div className="col-6 flex align-items-center gap-2">
          <Button
            icon="pi pi-arrow-left"
            className="p-button-text p-button-rounded"
            onClick={() => navigate('/community_moderation')}
            aria-label="Back"
          />
          <h2 className="m-0 main-text font-bold vertical-align-middle">
            <span>Review report</span>
          </h2>
        </div>
        <div className="col-6 flex justify-content-end align-items-center gap-2">
          <Tag
            value={report.status?.replace(/_/g, ' ')}
            severity={statusSeverity(report.status)}
            className="review-status-tag"
          />
          <Tag
            value={
              report.authorizationType === 'auto' ||
              report.moderation?.reviewedBy === 'auto-authorizer'
                ? 'Auto por sistema'
                : report.authorizationType === 'manual' || report.moderation?.reviewedBy
                  ? 'Manual'
                  : 'Pendiente'
            }
            severity={
              report.authorizationType === 'auto' ||
              report.moderation?.reviewedBy === 'auto-authorizer'
                ? 'info'
                : report.authorizationType === 'manual' || report.moderation?.reviewedBy
                  ? 'success'
                  : 'warning'
            }
          />
        </div>
      </div>

      {/* <section className="review-hero">
        {report.photoUrl ? (
          <img src={report.photoUrl} alt="Report" className="review-hero-image" />
        ) : (
          <div className="review-hero-empty">
            <i className="pi pi-image" />
            <span>Sin foto</span>
          </div>
        )}
        <div className="review-hero-overlay">
          <div className="review-hero-meta">
            <strong>{userName}</strong>
            <span>{crossing}</span>
          </div>
          <div className="review-chips">
            <span className="review-chip">{report.laneType}</span>
            <span className="review-chip">{report.transportMode}</span>
            <span className="review-chip">{report.pointsAwarded || 0} pts</span>
          </div>
        </div>
      </section> */}

      <section className="review-card review-share-card">
        <div className="review-card-head">
          <h2>
            <i className="pi pi-facebook" /> Copiar para Facebook
          </h2>
        </div>
        <p className="review-help">Copia la imagen y el caption por separado, luego pégalos en Facebook.</p>
        <div className="review-share-row">
          <Button
            label="Copiar imagen"
            icon="pi pi-image"
            className="review-share-btn"
            loading={copyingImage}
            disabled={!report.photoUrl}
            onClick={handleCopyImage}
          />
          <Button
            label="Copiar caption"
            icon="pi pi-align-left"
            outlined
            className="review-share-btn"
            loading={copyingCaption}
            onClick={handleCopyCaption}
          />
        </div>
      </section>

      {report.text?.trim() ? (
        <section className="review-card review-quote">
          <p>{report.text}</p>
        </section>
      ) : null}

      <section className="review-card">
        <div className="review-card-head">
          <h2>Detalles</h2>
          <span>{new Date(report.createdAt).toLocaleString()}</span>
        </div>
        <dl className="review-dl">
          <div>
            <dt>Usuario</dt>
            <dd>{userName}</dd>
          </div>
          <div>
            <dt>Garita</dt>
            <dd>{crossing}</dd>
          </div>
          <div>
            <dt>Carril</dt>
            <dd>
              {report.laneType} · {report.transportMode}
            </dd>
          </div>
          {report.waitBucketMinutes != null && (
            <div>
              <dt>Espera</dt>
              <dd>{formatWait(report.waitBucketMinutes)}</dd>
            </div>
          )}
          {report.waitingFromPositionMinutes != null && (
            <div>
              <dt>Desde posición</dt>
              <dd>{formatWait(report.waitingFromPositionMinutes)}</dd>
            </div>
          )}
        </dl>
      </section>

      <section className="review-card">
        <div className="review-card-head">
          <h2>Autorizar secciones</h2>
        </div>
        {report.moderation?.reviewedBy === 'auto-authorizer' ? (
          <p className="review-help">
            Este reporte ya pasó por el auto autorizador
            {report.moderation?.note ? `: ${report.moderation.note}` : '.'} Puedes
            seguir marcando o desmarcando secciones.
          </p>
        ) : (
          <p className="review-help">
            Desmarca lo que no quieras publicar. Lo desmarcado no sale en el feed.
          </p>
        )}
        <div className="review-sections">
          {sectionRows.map((row) => (
            <label
              key={row.key}
              htmlFor={row.key}
              className={`review-section ${!row.present ? 'is-empty' : ''} ${
                sections[row.key] ? 'is-on' : 'is-off'
              }`}
            >
              <Checkbox
                inputId={row.key}
                checked={!!sections[row.key]}
                disabled={!row.present}
                onChange={(e) => setSections({ ...sections, [row.key]: e.checked })}
              />
              <div className="review-section-body">
                <div className="review-section-title">
                  <span>{row.label}</span>
                  {!row.present && <Tag value="N/A" severity="secondary" />}
                </div>
                {row.key === 'photoApproved' && row.present ? (
                  <img src={row.preview} alt="" className="review-section-thumb" />
                ) : (
                  <p className="review-section-preview">{row.preview || '—'}</p>
                )}
              </div>
            </label>
          ))}
        </div>

        <div className="review-note">
          <label htmlFor="mod-note">Nota interna</label>
          <InputTextarea
            id="mod-note"
            rows={3}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            autoResize
          />
        </div>
      </section>

      <section className="review-card review-preview-card">
        <div className="review-card-head">
          <h2>Preview del feed</h2>
        </div>
        <div className="review-feed-preview">
          {sections.textApproved && report.text ? <p>{report.text}</p> : null}
          {sections.photoApproved && report.photoUrl ? (
            <img src={report.photoUrl} alt="preview" />
          ) : null}
          <div className="review-feed-tags">
            {sections.waitBucketApproved && report.waitBucketMinutes != null ? (
              <Tag value={`Wait ${formatWait(report.waitBucketMinutes)}`} />
            ) : null}
            {sections.waitingFromPositionApproved &&
            report.waitingFromPositionMinutes != null ? (
              <Tag value={`From ${formatWait(report.waitingFromPositionMinutes)}`} />
            ) : null}
          </div>
          {!sections.textApproved &&
            !sections.photoApproved &&
            !sections.waitBucketApproved && (
              <p className="review-help">Sin secciones visibles seleccionadas.</p>
            )}
        </div>
      </section>

      <div className="review-sticky-actions">
        <Button
          label="Rechazar"
          icon="pi pi-times"
          severity="danger"
          outlined
          className="review-action-reject"
          loading={saving}
          onClick={() => moderate('reject')}
        />
        <Button
          label="Aprobar"
          icon="pi pi-check"
          className="review-action-approve secondary-button"
          loading={saving}
          onClick={() => moderate('approve')}
        />
      </div>
      </div>
    </Card>
  );
}
